import { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, X, Check, RefreshCw, Moon, Sun } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import Button from '@/components/ui/Button';

interface AiThemeHelperProps {
  onApply: (palette: any, fonts?: { heading: string; body: string }) => void;
  onClose: () => void;
  context?: { storeName?: string; productType?: string };
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  palette?: any;
  dark?: any;
  fonts?: any;
}

function ColorSwatches({ colors, label }: { colors: Record<string, string>; label: string }) {
  return (
    <div className="mb-2">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="flex gap-1">
        {Object.entries(colors).map(([key, hex]) => (
          <div
            key={key}
            className="w-6 h-6 rounded-full border border-gray-200"
            style={{ backgroundColor: hex }}
            title={`${key}: ${hex}`}
          />
        ))}
      </div>
    </div>
  );
}

function PalettePreview({ palette, dark, previewDark }: { palette: any; dark?: any; previewDark: boolean }) {
  const activePalette = previewDark && dark ? dark : palette;
  if (!activePalette) return null;

  return (
    <div className={cn('mt-2 p-3 rounded-lg border', previewDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200')}>
      {activePalette.primary && (
        <ColorSwatches colors={activePalette.primary} label="Primary" />
      )}
      {activePalette.secondary && (
        <ColorSwatches colors={activePalette.secondary} label="Secondary" />
      )}
      {activePalette.accent && (
        <ColorSwatches colors={activePalette.accent} label="Accent" />
      )}
      {activePalette.text && (
        <ColorSwatches colors={activePalette.text} label="Text" />
      )}
      {activePalette.bg && (
        <ColorSwatches colors={activePalette.bg} label="Background" />
      )}
    </div>
  );
}

export default function AiThemeHelper({ onApply, onClose, context }: AiThemeHelperProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewDark, setPreviewDark] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const greeting = context?.storeName
      ? `Hi! I can help you create a theme for ${context.storeName}. Describe the look and feel you want, or I'll suggest a theme based on your site.`
      : 'Describe the look and feel you want for your store, and I\'ll generate a custom theme for you.';

    setMessages([{ role: 'assistant', content: greeting }]);
  }, [context?.storeName]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getLastPalette = (): any | undefined => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].palette) return messages[i].palette;
    }
    return undefined;
  };

  const handleSend = async (text?: string) => {
    const messageText = text || input.trim();
    if (!messageText || isGenerating) return;

    const userMessage: Message = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsGenerating(true);

    try {
      const lastPalette = getLastPalette();
      const payload: any = { description: messageText };
      if (lastPalette) {
        payload.current_palette = lastPalette;
        payload.refinement = messageText;
      }

      const response = await api.post<any>('/admin/ai/generate/theme', payload);

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.message || 'Here\'s a theme based on your description:',
        palette: response.palettes,
        dark: response.dark,
        fonts: response.fonts,
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I had trouble generating a theme. Please try again with a different description.' },
      ]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-[600px]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-500" />
          <h3 className="font-semibold text-gray-900">AI Theme Helper</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPreviewDark(!previewDark)}
            className="p-1.5 rounded-md hover:bg-gray-200 text-gray-500 transition-colors"
            title={previewDark ? 'Show light mode' : 'Show dark mode'}
          >
            {previewDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-200 text-gray-500 transition-colors"
            aria-label="Close AI Theme Helper"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
            <div
              className={cn(
                'max-w-[85%] rounded-lg px-3 py-2 text-sm',
                msg.role === 'user'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-800'
              )}
            >
              <p>{msg.content}</p>
              {msg.palette && (
                <>
                  <PalettePreview palette={msg.palette} dark={msg.dark} previewDark={previewDark} />
                  {msg.fonts && (
                    <div className="mt-2 text-xs text-gray-500">
                      Fonts: <span className="font-medium">{msg.fonts.heading}</span> / <span className="font-medium">{msg.fonts.body}</span>
                    </div>
                  )}
                  <div className="mt-2 flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => onApply(msg.palette, msg.fonts)}
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Apply This Theme
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setInput('Refine this theme: ');
                      }}
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Refine
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}

        {isGenerating && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg px-3 py-2 text-sm text-gray-500">
              <span className="inline-flex gap-1">
                <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
              </span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-gray-200 bg-white">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe your ideal theme..."
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            disabled={isGenerating}
          />
          <button
            onClick={() => handleSend()}
            disabled={!input.trim() || isGenerating}
            className={cn(
              'p-2 rounded-lg transition-colors',
              input.trim() && !isGenerating
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            )}
            aria-label="Send message"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
